"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Send, RotateCcw, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";

export const DEFAULT_GASTIFY_MESSAGES = [
  {
    id: "1",
    sender: "assistant",
    content:
      "Olá! Sou o Assistente Inteligente do Gastify. Como posso ajudar nas suas finanças hoje?",
  },
  {
    id: "2",
    sender: "user",
    content: "Acabei de almoçar no restaurante e paguei R$ 68,50 no cartão de crédito.",
  },
  {
    id: "3",
    sender: "assistant",
    content:
      "Lançamento realizado! Registrei R$ 68,50 em Alimentação no seu Cartão Principal. Sua fatura atual está em R$ 1.428,50.",
  },
  {
    id: "4",
    sender: "user",
    content: "Quanto ainda posso gastar esse mês sem sair da minha meta?",
  },
  {
    id: "5",
    sender: "assistant",
    content:
      "Você ainda tem R$ 820,00 livres no limite da sua meta. Mantendo sua média diária de R$ 45, você fechará o mês com R$ 280 economizados!",
  },
  {
    id: "6",
    sender: "user",
    content: "Excelente! Adiciona também R$ 120 de gasolina hoje.",
  },
  {
    id: "7",
    sender: "assistant",
    content:
      "Prontinho! R$ 120,00 lançados em Transporte. Seu painel e relatórios foram atualizados automaticamente.",
  },
];

function TypingIndicator({ className }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-2xl rounded-tl-md border border-white/10 bg-zinc-850 bg-zinc-800/90 px-4 py-3 backdrop-blur-sm",
        className,
      )}
    >
      <span className="text-[11px] text-zinc-400 font-medium mr-1 flex items-center gap-1">
        <Sparkles className="size-3 text-emerald-400 animate-spin" /> Gastify IA digitando
      </span>
      {[0, 1, 2].map((i) => (
        <motion.span
          key={i}
          className="h-1.5 w-1.5 rounded-full bg-emerald-400"
          animate={{ opacity: [0.3, 1, 0.3], y: [0, -3, 0] }}
          transition={{
            duration: 0.8,
            repeat: Infinity,
            delay: i * 0.15,
            ease: "easeInOut",
          }}
        />
      ))}
    </motion.div>
  );
}

function MessageBubble({ message, isLast }) {
  const isUser = message.sender === "user";

  return (
    <motion.div
      initial={{ opacity: 0, y: 12, scale: 0.96, x: isUser ? 20 : -20 }}
      animate={{ opacity: 1, y: 0, scale: 1, x: 0 }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      className={cn("flex w-full", isUser ? "justify-end" : "justify-start")}
    >
      <div className={cn("flex items-end gap-2.5", isUser && "flex-row-reverse")}>
        {!isUser && (
          <div className="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-slate-900 border border-emerald-500/30 overflow-hidden shadow-xs">
            <img
              src="/gastify-icon-192.png"
              alt="Gastify IA"
              className="h-full w-full object-contain p-0.5"
            />
          </div>
        )}
        <motion.div
          layout
          className={cn(
            "max-w-[82%] sm:max-w-[78%] rounded-2xl px-4 py-2.5 text-xs sm:text-sm leading-relaxed",
            isUser
              ? "rounded-tr-md bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-[0_8px_24px_-4px_rgba(16,185,129,0.35)]"
              : "rounded-tl-md border border-white/10 bg-zinc-800/90 text-zinc-100 backdrop-blur-sm shadow-[0_4px_12px_-2px_rgba(0,0,0,0.3)]",
          )}
          whileHover={{ scale: 1.01, y: -1 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
        >
          {message.content}
        </motion.div>
      </div>
    </motion.div>
  );
}

export function ChatMessages({
  messages = DEFAULT_GASTIFY_MESSAGES,
  autoPlay = true,
  autoPlayDelay = 2200,
  typingDuration = 1200,
  showReplay = true,
  interactive = true,
  className,
}) {
  const [visibleCount, setVisibleCount] = useState(
    autoPlay ? 0 : messages.length,
  );
  const [isTyping, setIsTyping] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [chatMessages, setChatMessages] = useState(messages);
  const scrollRef = useRef(null);
  const isAutoPlaying = useRef(false);

  const scrollToBottom = useCallback(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, []);

  const revealNext = useCallback(
    async (index) => {
      if (index >= chatMessages.length) {
        isAutoPlaying.current = false;
        return;
      }

      const message = chatMessages[index];

      if (message.sender === "assistant") {
        setIsTyping(true);
        await new Promise((r) => setTimeout(r, typingDuration));
        setIsTyping(false);
      }

      setVisibleCount(index + 1);
      await new Promise((r) => setTimeout(r, 100));
      scrollToBottom();

      await new Promise((r) =>
        setTimeout(
          r,
          Math.max(
            300,
            autoPlayDelay -
              (message.sender === "assistant" ? typingDuration : 0) -
              100,
          ),
        ),
      );

      if (isAutoPlaying.current) {
        revealNext(index + 1);
      }
    },
    [chatMessages, autoPlayDelay, typingDuration, scrollToBottom],
  );

  const replay = useCallback(() => {
    setVisibleCount(0);
    setChatMessages(messages);
    isAutoPlaying.current = true;
    setTimeout(() => revealNext(0), 100);
  }, [messages, revealNext]);

  useEffect(() => {
    setChatMessages(messages);
    if (autoPlay) {
      setVisibleCount(0);
      isAutoPlaying.current = true;
      const timer = setTimeout(() => revealNext(0), 500);
      return () => {
        clearTimeout(timer);
        isAutoPlaying.current = false;
      };
    } else {
      setVisibleCount(messages.length);
    }
  }, [messages, autoPlay, revealNext]);

  useEffect(() => {
    scrollToBottom();
  }, [visibleCount, isTyping, scrollToBottom]);

  const handleSend = useCallback(() => {
    if (!inputValue.trim() || !interactive) return;

    const userText = inputValue.trim();
    const newMessage = {
      id: `user-${Date.now()}`,
      sender: "user",
      content: userText,
    };

    setChatMessages((prev) => [...prev, newMessage]);
    setInputValue("");
    setVisibleCount((prev) => prev + 1);

    setTimeout(() => {
      setIsTyping(true);
      setTimeout(() => {
        setIsTyping(false);
        const assistantReply = {
          id: `assistant-${Date.now()}`,
          sender: "assistant",
          content: `Entendido! Registrei "${userText}" com sucesso no seu Gastify. Suas metas e saldo foram recalculados!`,
        };
        setChatMessages((prev) => [...prev, assistantReply]);
        setVisibleCount((prev) => prev + 1);
      }, 1000);
    }, 300);
  }, [inputValue, interactive]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div
      className={cn(
        "relative flex flex-col overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-b from-zinc-900 to-zinc-950 shadow-[0_24px_64px_-16px_rgba(0,0,0,0.6)]",
        className,
      )}
    >
      {/* Header do Chat com Logotipo Oficial Gastify */}
      <div className="flex items-center justify-between border-b border-white/10 px-4 sm:px-5 py-3.5 bg-zinc-900/60 backdrop-blur-md">
        <div className="flex items-center gap-3">
          <div className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-slate-950 border border-emerald-500/40 p-1 shadow-xs">
            <img
              src="/gastify-icon-192.png"
              alt="Gastify"
              className="h-full w-full object-contain"
            />
            <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-500 border-2 border-zinc-900" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-extrabold text-white tracking-tight">Gastify IA</h3>
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/20 px-2 py-0.5 text-[9px] font-black text-emerald-300 border border-emerald-500/30">
                <Sparkles size={10} /> Online
              </span>
            </div>
            <p className="text-[11px] text-zinc-400">Assistente Financeiro Consciente</p>
          </div>
        </div>

        {showReplay && (
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={replay}
            aria-label="Reiniciar demonstração"
            className="flex items-center gap-1.5 rounded-xl bg-white/5 border border-white/10 px-3 py-1.5 text-xs font-semibold text-zinc-300 transition-colors hover:bg-white/10 hover:text-white cursor-pointer"
          >
            <RotateCcw className="size-3.5 text-emerald-400" />
            <span className="hidden sm:inline">Reiniciar</span>
          </motion.button>
        )}
      </div>

      {/* Área de Mensagens com rolagem suave */}
      <div
        ref={scrollRef}
        role="log"
        aria-label="Mensagens do chat"
        aria-live="polite"
        className="flex-1 space-y-3.5 overflow-y-auto p-4 sm:p-5 scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent min-h-[320px] max-h-[460px]"
      >
        {chatMessages.slice(0, visibleCount).map((message, i) => (
          <MessageBubble
            key={message.id}
            message={message}
            isLast={i === visibleCount - 1}
          />
        ))}

        <AnimatePresence>{isTyping && <TypingIndicator />}</AnimatePresence>
      </div>

      {/* Input de Mensagem Interativo */}
      <div className="border-t border-white/10 p-3 sm:p-4 bg-zinc-950/80 backdrop-blur-md">
        <div className="flex items-center gap-2 rounded-2xl border border-white/10 bg-zinc-800/60 px-4 py-2.5 backdrop-blur-sm focus-within:border-emerald-500/50 focus-within:bg-zinc-800/90 transition-all shadow-inner">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Digite como se falasse com um amigo: 'gastei 50 no mercado'..."
            aria-label="Mensagem para o Gastify IA"
            className="flex-1 bg-transparent text-xs sm:text-sm text-white outline-none placeholder:text-zinc-500"
          />
          <motion.button
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.92 }}
            onClick={handleSend}
            disabled={!inputValue.trim()}
            aria-label="Enviar mensagem"
            className={cn(
              "flex h-8 w-8 items-center justify-center rounded-xl transition-all cursor-pointer",
              inputValue.trim()
                ? "bg-gradient-to-tr from-emerald-500 to-teal-500 text-slate-950 shadow-md shadow-emerald-500/30"
                : "bg-white/5 text-zinc-500 cursor-not-allowed",
            )}
          >
            <Send className="size-3.5" />
          </motion.button>
        </div>
      </div>
    </div>
  );
}

export default ChatMessages;
